import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Heart, Lock } from "lucide-react";
import { api, eur, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import Footer from "../components/Footer";

const EMPTY_FORM = {
  name: "", surname: "", email: "",
  street: "", zip: "", city: "", province: "", phone: "", delivery_notes: "",
};

const BRAND_LABELS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express" };

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [payMethods, setPayMethods] = useState(["card"]);

  useEffect(() => {
    api.get("/payments/methods")
      .then((r) => setPayMethods(r.data.methods || ["card"]))
      .catch(() => {});
  }, []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveAddress, setSaveAddress] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [cards, setCards] = useState([]);
  const [payWith, setPayWith] = useState("new");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, name: user.name || "", surname: user.surname || "", email: user.email || "" }));
    api.get("/account/addresses").then((r) => {
      const def = (r.data || []).find((a) => a.is_default);
      if (def) {
        setForm((f) => ({
          ...f,
          name: def.name || f.name, surname: def.surname || f.surname,
          street: def.street || "", zip: def.zip || "", city: def.city || "",
          province: def.province || "", phone: def.phone || "",
        }));
      }
    }).catch(() => {});
    api.get("/account/cards").then((r) => {
      const list = r.data.cards || [];
      setCards(list);
      const def = list.find((c) => c.is_default);
      if (def) setPayWith(def.id);
    }).catch(() => {});
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payloadItems = items.map((i) => ({ product_id: i.id, quantity: i.quantity }));
    try {
      if (payWith !== "new") {
        const { data } = await api.post("/payments/pay-with-saved", {
          payment_method_id: payWith,
          items: payloadItems,
          customer: form,
          origin_url: window.location.origin,
        });
        if (data.status === "succeeded") {
          clear();
          navigate(`/payment/success?saved=1&order=${data.order_id}`);
        } else {
          setError("Pagamento non completato: la banca richiede una nuova autenticazione. Usa una nuova carta.");
          setBusy(false);
        }
      } else {
        const { data } = await api.post("/payments/checkout", {
          items: payloadItems,
          origin_url: window.location.origin,
          customer: form,
          save_card: saveCard,
          save_address: saveAddress,
        });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="checkout-empty">
        <h1 className="font-serif font-light text-4xl mb-6">Il carrello è vuoto</h1>
        <Link to="/shop" className="text-primary text-sm tracking-wide" data-testid="checkout-empty-link">
          Scopri la collezione
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-20" data-testid="checkout-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-14 md:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="overline-label mb-5">Ultimo passo</p>
          <h1 className="font-serif font-light text-4xl md:text-6xl mb-16">
            <span className="italic text-primary">Checkout</span>
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-16 items-start">
          <form onSubmit={submit} className="lg:col-span-3 space-y-10" data-testid="checkout-form">
            {user && cards.length > 0 && (
              <div className="border border-white/10 bg-card p-7 space-y-4" data-testid="saved-cards-section">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Usa una carta salvata
                </p>
                {cards.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    data-testid={`saved-card-${c.id}`}
                    onClick={() => setPayWith(c.id)}
                    className={`w-full flex items-center gap-4 border px-5 py-4 text-left transition-colors duration-300 ${
                      payWith === c.id ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <CreditCard size={18} className="text-primary flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm capitalize">
                      {BRAND_LABELS[c.brand] || c.brand} •••• {c.last4}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {String(c.exp_month).padStart(2, "0")}/{c.exp_year}
                    </span>
                    {c.is_default && (
                      <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-primary">
                        Predefinita
                      </span>
                    )}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="use-new-card"
                  onClick={() => setPayWith("new")}
                  className={`w-full flex items-center gap-4 border px-5 py-4 text-left transition-colors duration-300 ${
                    payWith === "new" ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <CreditCard size={18} className="text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-muted-foreground">Usa una nuova carta</span>
                </button>
              </div>
            )}

            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">Contatti</p>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="co-name" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nome</Label>
                    <Input id="co-name" data-testid="checkout-name-input" required value={form.name} onChange={set("name")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-surname" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cognome</Label>
                    <Input id="co-surname" data-testid="checkout-surname-input" required value={form.surname} onChange={set("surname")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="co-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                  <Input id="co-email" data-testid="checkout-email-input" type="email" required value={form.email} onChange={set("email")}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">Indirizzo di consegna</p>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="co-street" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Via e numero civico</Label>
                  <Input id="co-street" data-testid="checkout-street-input" required value={form.street} onChange={set("street")}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="co-zip" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CAP</Label>
                    <Input id="co-zip" data-testid="checkout-zip-input" required value={form.zip} onChange={set("zip")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-province" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Provincia</Label>
                    <Input id="co-province" data-testid="checkout-province-input" required maxLength={50} value={form.province} onChange={set("province")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="co-city" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Comune</Label>
                    <Input id="co-city" data-testid="checkout-city-input" required value={form.city} onChange={set("city")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co-phone" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Telefono</Label>
                    <Input id="co-phone" data-testid="checkout-phone-input" value={form.phone} onChange={set("phone")}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="co-notes" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Note di consegna (facoltative)</Label>
                  <Input id="co-notes" data-testid="checkout-notes-input" placeholder='Es. "Citofono Rossi, 3° piano"' value={form.delivery_notes} onChange={set("delivery_notes")}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary placeholder:text-muted-foreground/50" />
                </div>
              </div>
            </div>

            {user && (
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="save-address-label">
                  <Checkbox
                    data-testid="save-address-checkbox"
                    checked={saveAddress}
                    onCheckedChange={(v) => setSaveAddress(!!v)}
                    className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground">Salva questo indirizzo nel mio profilo</span>
                </label>
                {payWith === "new" && (
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="save-card-label">
                    <Checkbox
                      data-testid="save-card-checkbox"
                      checked={saveCard}
                      onCheckedChange={(v) => setSaveCard(!!v)}
                      className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-0.5"
                    />
                    <span className="text-sm text-muted-foreground">Salva questa carta per i prossimi acquisti (pagamento con carta)</span>
                  </label>
                )}
              </div>
            )}

            {error && <p className="text-sm text-destructive" data-testid="checkout-error">{error}</p>}

            <button
              type="submit"
              disabled={busy}
              data-testid="checkout-pay-button"
              className="inline-flex items-center gap-3 h-14 px-10 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              <Lock size={14} strokeWidth={1.5} />
              {busy ? "Elaborazione…" : `Paga ${eur(total)}`}
              <span className="inline-flex items-center gap-1.5" data-testid="pay-button-method-icons">
                {payMethods.map((m) => (
                  <span
                    key={m}
                    title={m === "card" ? "Carta" : m === "klarna" ? "Klarna" : "Scalapay"}
                    className="inline-flex items-center justify-center h-6 min-w-6 px-1 rounded-md bg-[#09090B]"
                  >
                    {m === "card" && <CreditCard size={13} className="text-primary" />}
                    {m === "klarna" && <span className="text-[10px] font-bold leading-none text-[#FFB3C7]">K.</span>}
                    {m === "scalapay" && <Heart size={11} className="text-[#7BD88A]" fill="currentColor" />}
                  </span>
                ))}
              </span>
            </button>
            <p className="text-xs text-muted-foreground tracking-wide" data-testid="checkout-secure-hint">
              Pagamento sicuro su Stripe. I dati della carta non passano mai dai nostri server.
            </p>
            <div className="flex flex-wrap gap-2 pt-1" data-testid="checkout-payment-methods">
              {["Visa", "Mastercard", "Apple Pay", "Google Pay", "Klarna", "Scalapay"].map((m) => (
                <span key={m} className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground border border-white/10 rounded-full px-3 py-1.5">{m}</span>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70 tracking-wide" data-testid="checkout-vendor">
              Venditore: AB fëlise shop - P.IVA 03383950809
            </p>
          </form>

          <aside className="lg:col-span-2 border border-white/10 bg-card p-8 md:p-10" data-testid="checkout-summary">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-8">
              Riepilogo ordine
            </p>
            <div className="space-y-6 mb-8">
              {items.map((i) => (
                <div key={i.id} className="flex gap-4 items-center" data-testid={`summary-item-${i.id}`}>
                  <div className="w-14 h-16 overflow-hidden flex-shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{i.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">Quantità {i.quantity}</p>
                  </div>
                  <p className="text-sm">{eur(i.price * i.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-6 space-y-3">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Subtotale</span>
                <span>{eur(total)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Consegna</span>
                <span>Inclusa</span>
              </div>
              <div className="flex justify-between items-baseline pt-3">
                <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Totale</span>
                <span className="font-serif text-3xl" data-testid="checkout-total">{eur(total)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </main>
  );
}
